import React, { useEffect, useRef, useState } from 'react';

interface CosmicLoadingScreenProps {
  progress: number;
  message: string;
  phase?: string;
}

const CosmicLoadingScreen: React.FC<CosmicLoadingScreenProps> = ({ 
  progress, 
  message,
  phase 
}) => {
  const cosmicCanvasRef = useRef<HTMLCanvasElement>(null);
  const sacredGeometryCanvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const particlesRef = useRef<any[]>([]);
  const wavesRef = useRef<any[]>([]);
  const timeRef = useRef<number>(0);
  const [isFadingOut, setIsFadingOut] = useState(false);

  // Status messages that rotate
  const statusMessages = [
    'Initializing cosmic matrix...',
    'Calibrating frequencies...',
    'Harmonizing sacred geometry...',
    'Connecting to infinite potential...',
    'Preparing Fr00t experience...'
  ];
  const [statusIndex, setStatusIndex] = useState(0);

  useEffect(() => {
    // Rotate status messages
    const statusInterval = setInterval(() => {
      setStatusIndex((prev) => (prev + 1) % statusMessages.length);
    }, 2000);

    return () => clearInterval(statusInterval);
  }, []);

  // Fade out when progress reaches 100%
  useEffect(() => {
    if (progress >= 100 && !isFadingOut) {
      // Wait a moment to show 100%, then fade out
      const fadeTimer = setTimeout(() => {
        setIsFadingOut(true);
      }, 1000);
      return () => clearTimeout(fadeTimer);
    }
  }, [progress, isFadingOut]);

  useEffect(() => {
    const cosmicCanvas = cosmicCanvasRef.current;
    const sacredCanvas = sacredGeometryCanvasRef.current;
    if (!cosmicCanvas || !sacredCanvas) return;

    const ctx = cosmicCanvas.getContext('2d');
    const sgCtx = sacredCanvas.getContext('2d');
    if (!ctx || !sgCtx) return;

    // Set canvas sizes
    const resizeCanvases = () => {
      cosmicCanvas.width = window.innerWidth;
      cosmicCanvas.height = window.innerHeight;
      
      const container = sacredCanvas.parentElement;
      if (container) {
        sacredCanvas.width = container.clientWidth;
        sacredCanvas.height = container.clientHeight;
      }
    };
    resizeCanvases();

    // Mathematical constants
    const PHI = (1 + Math.sqrt(5)) / 2; // Golden ratio
    const TAU = Math.PI * 2;

    // Particle class
    class Particle {
      x: number = 0;
      y: number = 0;
      size: number = 0;
      speedX: number = 0;
      speedY: number = 0;
      hue: number = 0;
      alpha: number = 0;
      life: number = 0;
      maxLife: number = 0;
      private canvas: HTMLCanvasElement;

      constructor(canvas: HTMLCanvasElement) {
        this.canvas = canvas;
        this.reset();
      }

      reset() {
        this.x = Math.random() * this.canvas.width;
        this.y = Math.random() * this.canvas.height;
        this.size = Math.random() * 1.5 + 0.3;
        this.speedX = Math.random() * 0.9 - 0.45; // 3x speed
        this.speedY = Math.random() * 0.9 - 0.45; // 3x speed
        this.hue = Math.random() * 360;
        this.alpha = Math.random() * 0.3 + 0.1;
        this.life = Math.random() * 300 + 200;
        this.maxLife = this.life;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        this.life--;
        this.alpha = (this.life / this.maxLife) * 0.4;

        if (this.life <= 0 || this.x < 0 || this.x > this.canvas.width || 
            this.y < 0 || this.y > this.canvas.height) {
          this.reset();
        }
      }

      draw() {
        if (!ctx) return;
        ctx.save();
        ctx.globalAlpha = this.alpha;
        ctx.fillStyle = `hsl(${this.hue}, 100%, 60%)`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, TAU);
        ctx.fill();
        ctx.restore();
      }
    }

    // Wave class
    class Wave {
      y: number;
      amplitude: number;
      frequency: number;
      speed: number;
      hue: number;
      offset: number = 0;
      private canvas: HTMLCanvasElement;

      constructor(canvas: HTMLCanvasElement, y: number, amplitude: number, frequency: number, speed: number, hue: number) {
        this.canvas = canvas;
        this.y = y;
        this.amplitude = amplitude;
        this.frequency = frequency;
        this.speed = speed;
        this.hue = hue;
      }

      update() {
        this.offset += this.speed;
      }

      draw() {
        if (!ctx) return;
        ctx.save();
        ctx.strokeStyle = `hsla(${this.hue}, 100%, 50%, 0.15)`;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        
        for (let x = 0; x < this.canvas.width; x += 3) {
          const y = this.y + Math.sin((x * this.frequency) + this.offset) * this.amplitude;
          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        
        ctx.stroke();
        ctx.restore();
      }
    }

    // Initialize particles and waves
    particlesRef.current = Array.from({ length: 100 }, () => new Particle(cosmicCanvas));
    wavesRef.current = [
      new Wave(cosmicCanvas, cosmicCanvas.height * 0.25, 40, 0.008, 0.045, 200), // 3x speed
      new Wave(cosmicCanvas, cosmicCanvas.height * 0.5, 60, 0.005, 0.036, 280), // 3x speed
      new Wave(cosmicCanvas, cosmicCanvas.height * 0.75, 50, 0.01, 0.06, 320), // 3x speed
    ];

    // Draw sacred geometry
    const drawSacredGeometry = () => {
      if (!sgCtx) return;
      sgCtx.fillStyle = 'rgba(0, 0, 0, 0.02)';
      sgCtx.fillRect(0, 0, sacredCanvas.width, sacredCanvas.height);

      const centerX = sacredCanvas.width / 2;
      const centerY = sacredCanvas.height / 2;
      const baseRadius = Math.min(sacredCanvas.width, sacredCanvas.height) * 0.15;

      // Draw Flower of Life pattern
      sgCtx.strokeStyle = `hsla(${(timeRef.current * 1.5) % 360}, 70%, 60%, 0.3)`; // 3x speed
      sgCtx.lineWidth = 1;
      
      const circles = [
        { x: 0, y: 0 },
        { x: baseRadius, y: 0 },
        { x: baseRadius / 2, y: baseRadius * Math.sqrt(3) / 2 },
        { x: -baseRadius / 2, y: baseRadius * Math.sqrt(3) / 2 },
        { x: -baseRadius, y: 0 },
        { x: -baseRadius / 2, y: -baseRadius * Math.sqrt(3) / 2 },
        { x: baseRadius / 2, y: -baseRadius * Math.sqrt(3) / 2 },
      ];

      circles.forEach(offset => {
        sgCtx.beginPath();
        sgCtx.arc(centerX + offset.x, centerY + offset.y, baseRadius, 0, TAU);
        sgCtx.stroke();
      });

      // Draw Fibonacci spiral
      sgCtx.strokeStyle = `hsla(${(timeRef.current * 0.9 + 120) % 360}, 80%, 50%, 0.4)`; // 3x speed
      sgCtx.lineWidth = 1.5;
      sgCtx.beginPath();
      
      const angle = timeRef.current * 0.03; // 3x speed
      const spiralTurns = 5;
      for (let i = 0; i < 200; i++) {
        const t = i / 200 * spiralTurns * TAU;
        const r = baseRadius * 0.2 * Math.pow(PHI, t / TAU);
        const x = centerX + r * Math.cos(t + angle);
        const y = centerY + r * Math.sin(t + angle);
        
        if (i === 0) sgCtx.moveTo(x, y);
        else sgCtx.lineTo(x, y);
      }
      sgCtx.stroke();

      // Draw cymatic frequency circles
      for (let i = 1; i <= 7; i++) {
        const radius = baseRadius * i * 0.4;
        const alpha = 0.15 / i;
        sgCtx.strokeStyle = `hsla(${(timeRef.current * 3 + i * 30) % 360}, 90%, 60%, ${alpha})`; // 3x speed
        sgCtx.lineWidth = 1;
        sgCtx.beginPath();
        sgCtx.arc(centerX, centerY, radius, 0, TAU);
        sgCtx.stroke();
      }

      // Draw standing wave patterns
      const waveCount = 8;
      for (let w = 0; w < waveCount; w++) {
        sgCtx.strokeStyle = `hsla(${(timeRef.current * 2.1 + w * 45) % 360}, 80%, 50%, 0.2)`; // 3x speed
        sgCtx.lineWidth = 0.8;
        sgCtx.beginPath();
        
        for (let a = 0; a < TAU; a += 0.1) {
          const frequency = w + 3;
          const modulation = Math.sin(frequency * a + timeRef.current * 0.06) * 20; // 3x speed
          const r = baseRadius * 1.5 + modulation;
          const x = centerX + r * Math.cos(a);
          const y = centerY + r * Math.sin(a);
          
          if (a === 0) sgCtx.moveTo(x, y);
          else sgCtx.lineTo(x, y);
        }
        sgCtx.closePath();
        sgCtx.stroke();
      }

      // Draw Metatron's Cube vertices
      sgCtx.fillStyle = `hsla(${(timeRef.current * 1.2 + 180) % 360}, 100%, 70%, 0.4)`; // 3x speed
      const metatronRadius = baseRadius * 1.8;
      for (let i = 0; i < 13; i++) {
        let x, y;
        if (i === 0) {
          x = centerX;
          y = centerY;
        } else {
          const angle = (i - 1) / 6 * Math.PI + timeRef.current * 0.015; // 3x speed
          const r = i <= 6 ? metatronRadius * 0.5 : metatronRadius;
          x = centerX + r * Math.cos(angle);
          y = centerY + r * Math.sin(angle);
        }
        
        sgCtx.beginPath();
        sgCtx.arc(x, y, 2, 0, TAU);
        sgCtx.fill();
      }

      timeRef.current += 3; // 3x speed
    };

    // Animation loop
    const animate = () => {
      if (!ctx || !sgCtx) return;

      // Cosmic canvas animation
      ctx.fillStyle = 'rgba(0, 0, 0, 0.03)';
      ctx.fillRect(0, 0, cosmicCanvas.width, cosmicCanvas.height);

      // Draw minimal grid
      ctx.save();
      ctx.strokeStyle = 'rgba(100, 50, 200, 0.05)';
      ctx.lineWidth = 0.5;
      const gridSize = 80;
      for (let x = 0; x < cosmicCanvas.width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, cosmicCanvas.height);
        ctx.stroke();
      }
      for (let y = 0; y < cosmicCanvas.height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(cosmicCanvas.width, y);
        ctx.stroke();
      }
      ctx.restore();

      // Update and draw waves
      wavesRef.current.forEach(wave => {
        wave.update();
        wave.draw();
      });

      // Update and draw particles
      particlesRef.current.forEach(particle => {
        particle.update();
        particle.draw();
      });

      // Draw sacred geometry
      drawSacredGeometry();

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Handle resize
    const handleResize = () => {
      resizeCanvases();
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <div 
      className="cosmic-loading-screen"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 10000,
        background: '#000000',
        opacity: isFadingOut ? 0 : 1,
        visibility: isFadingOut ? 'hidden' : 'visible',
        transition: 'opacity 2s ease-out, visibility 2s',
        pointerEvents: isFadingOut ? 'none' : 'auto'
      }}
    >
      <canvas 
        ref={cosmicCanvasRef}
        id="cosmic-canvas"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 1,
          background: 'radial-gradient(ellipse at center, #0a0520 0%, #000000 100%)'
        }}
      />
      
      <div className="cosmic-content-wrapper" style={{
        position: 'relative',
        zIndex: 10,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <h1 className="cosmic-logo" style={{
          fontFamily: "'Orbitron', sans-serif",
          fontSize: 'clamp(4rem, 12vw, 10rem)',
          fontWeight: 900,
          background: 'linear-gradient(45deg, #ff0080, #ff8c00, #ffd700, #00ff00, #00ffff, #0080ff, #8000ff, #ff0080)',
          backgroundSize: '400% 400%',
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          animation: 'gradientShift 2.67s ease infinite, float 2s ease-in-out infinite', // 3x speed
          textAlign: 'center',
          letterSpacing: '0.1em',
          textShadow: '0 0 40px rgba(255, 255, 255, 0.3)',
          marginBottom: '1rem'
        }}>
          Fr00t
        </h1>
        
        <div className="spectrum-bar" style={{
          width: '100%',
          maxWidth: '900px',
          height: '4px',
          margin: '3rem auto',
          background: 'linear-gradient(90deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #9400d3, #ff0000)',
          backgroundSize: '200% 100%',
          animation: 'spectrumFlow 3.33s linear infinite', // 3x speed
          borderRadius: '2px',
          boxShadow: '0 0 20px rgba(255, 255, 255, 0.5)'
        }} />
        
        <div className="math-canvas-container" style={{
          width: '90vw',
          maxWidth: '800px',
          height: '60vh',
          margin: '2rem auto',
          position: 'relative'
        }}>
          <canvas 
            ref={sacredGeometryCanvasRef}
            id="sacred-geometry"
            style={{
              width: '100%',
              height: '100%',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '4px'
            }}
          />
        </div>

        <div className="frequency-display" style={{
          fontFamily: "'Orbitron', monospace",
          fontSize: 'clamp(0.9rem, 2vw, 1.2rem)',
          textAlign: 'center',
          margin: '2rem auto',
          opacity: 0.6,
          letterSpacing: '0.2em',
          color: 'rgba(255, 255, 255, 0.7)'
        }}>
          <div className="freq-label" style={{
            display: 'inline-block',
            padding: '0.5rem 1.5rem',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '2px'
          }}>
            λ = c/ν
          </div>
        </div>

        <div className="loading-progress" style={{
          position: 'absolute',
          bottom: '4rem',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '90%',
          maxWidth: '500px',
          height: '2px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '2px',
          overflow: 'hidden'
        }}>
          <div 
            className="loading-progress-bar"
            style={{
              height: '100%',
              background: 'linear-gradient(90deg, #ff0080, #ff8c00, #ffd700, #00ff00, #00ffff, #0080ff, #8000ff, #ff0080)',
              backgroundSize: '200% 100%',
              animation: 'spectrumFlow 0.67s linear infinite', // 3x speed
              width: `${progress}%`,
              transition: 'width 0.3s ease-out'
            }}
          />
        </div>
        
        <div className="loading-status" style={{
          position: 'absolute',
          bottom: '2rem',
          left: '50%',
          transform: 'translateX(-50%)',
          fontFamily: "'Orbitron', monospace",
          fontSize: '0.9rem',
          color: 'rgba(255, 255, 255, 0.6)',
          textAlign: 'center',
          letterSpacing: '0.1em'
        }}>
          {statusMessages[statusIndex]} {phase && `• ${phase}`}
        </div>
      </div>

      <style>{`
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes spectrumFlow {
          0% { background-position: 0% 0%; }
          100% { background-position: 200% 0%; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>
    </div>
  );
};

export default CosmicLoadingScreen;

